'use client';

import { useState } from 'react';
import { cn } from '../../lib/utils';

export function DataTransformer() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [operation, setOperation] = useState('base64-encode');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const operations = {
    // Encoding
    'base64-encode': {
      name: 'Base64 Encode',
      category: 'Encoding',
      fn: (input) => btoa(unescape(encodeURIComponent(input))),
    },
    'base64-decode': {
      name: 'Base64 Decode',
      category: 'Encoding',
      fn: (input) => decodeURIComponent(escape(atob(input))),
    },
    'url-encode': {
      name: 'URL Encode',
      category: 'Encoding',
      fn: (input) => encodeURIComponent(input),
    },
    'url-decode': {
      name: 'URL Decode',
      category: 'Encoding',
      fn: (input) => decodeURIComponent(input),
    },
    'hex-encode': {
      name: 'To Hex',
      category: 'Encoding',
      fn: (input) => Array.from(new TextEncoder().encode(input))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join(''),
    },
    'hex-decode': {
      name: 'From Hex',
      category: 'Encoding',
      fn: (input) => {
        const hex = input.replace(/\s/g, '');
        const bytes = [];
        for (let i = 0; i < hex.length; i += 2) {
          bytes.push(parseInt(hex.substr(i, 2), 16));
        }
        return new TextDecoder().decode(new Uint8Array(bytes));
      },
    },

    // Hashing
    'md5': {
      name: 'MD5 Hash',
      category: 'Hashing',
      fn: async (input) => {
        // Simple MD5 implementation using crypto API
        const msgBuffer = new TextEncoder().encode(input);
        const hashBuffer = await crypto.subtle.digest('MD5', msgBuffer);
        return Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
      },
    },
    'sha1': {
      name: 'SHA-1 Hash',
      category: 'Hashing',
      fn: async (input) => {
        const msgBuffer = new TextEncoder().encode(input);
        const hashBuffer = await crypto.subtle.digest('SHA-1', msgBuffer);
        return Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
      },
    },
    'sha256': {
      name: 'SHA-256 Hash',
      category: 'Hashing',
      fn: async (input) => {
        const msgBuffer = new TextEncoder().encode(input);
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
        return Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
      },
    },
    'sha512': {
      name: 'SHA-512 Hash',
      category: 'Hashing',
      fn: async (input) => {
        const msgBuffer = new TextEncoder().encode(input);
        const hashBuffer = await crypto.subtle.digest('SHA-512', msgBuffer);
        return Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
      },
    },

    // Text Operations
    'uppercase': {
      name: 'To Uppercase',
      category: 'Text',
      fn: (input) => input.toUpperCase(),
    },
    'lowercase': {
      name: 'To Lowercase',
      category: 'Text',
      fn: (input) => input.toLowerCase(),
    },
    'reverse': {
      name: 'Reverse Text',
      category: 'Text',
      fn: (input) => input.split('').reverse().join(''),
    },
    'count-chars': {
      name: 'Count Characters',
      category: 'Text',
      fn: (input) => {
        return `Zeichen: ${input.length}\nWörter: ${input.split(/\s+/).filter(w => w).length}\nZeilen: ${input.split('\n').length}`;
      },
    },

    // JSON
    'json-beautify': {
      name: 'JSON Beautify',
      category: 'JSON',
      fn: (input) => JSON.stringify(JSON.parse(input), null, 2),
    },
    'json-minify': {
      name: 'JSON Minify',
      category: 'JSON',
      fn: (input) => JSON.stringify(JSON.parse(input)),
    },
  };

  const categories = [...new Set(Object.values(operations).map(op => op.category))];

  const processInput = async () => {
    if (!input) {
      setOutput('');
      setError('');
      return;
    }

    try {
      const op = operations[operation];
      const result = await op.fn(input);
      setOutput(result);
      setError('');
    } catch (err) {
      setError(`Fehler: ${err.message}`);
      setOutput('');
    }
  };

  const copyToClipboard = async () => {
    if (!output) return;

    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const swapInputOutput = () => {
    const temp = input;
    setInput(output);
    setOutput(temp);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="font-heading font-bold text-2xl mb-4">
        Data Transformer
      </h3>
      <p className="text-gray-700 mb-6">
        Encoding, Decoding, Hashing und Text-Transformationen. Inspiriert von CyberChef, optimiert für häufige Anwendungsfälle.
      </p>

      {/* Operation Selection by Category */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-3">
          Operation wählen
        </label>
        {categories.map(category => (
          <div key={category} className="mb-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {category}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {Object.entries(operations)
                .filter(([_, op]) => op.category === category)
                .map(([key, op]) => (
                  <button
                    key={key}
                    onClick={() => setOperation(key)}
                    className={cn(
                      'px-3 py-2 rounded text-sm font-semibold transition-colors',
                      operation === key
                        ? 'bg-accent text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                  >
                    {op.name}
                  </button>
                ))}
            </div>
          </div>
        ))}
      </div>

      {/* Input Section */}
      <div className="space-y-4 mb-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="data-input" className="block text-sm font-semibold">
              Eingabe
            </label>
            <span className="text-xs text-gray-500">
              {operations[operation].name}
            </span>
          </div>
          <textarea
            id="data-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Gib deine Daten hier ein..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent font-mono text-sm min-h-[200px]"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={processInput}
            className="bg-accent hover:bg-accent-dark text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Ausführen
          </button>
          {output && (
            <button
              onClick={swapInputOutput}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              ↕️ Input/Output tauschen
            </button>
          )}
          <button
            onClick={clearAll}
            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Leeren
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">
            <strong>⚠️ Fehler:</strong> {error}
          </p>
        </div>
      )}

      {/* Output Section */}
      {output && (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold">
                Ergebnis
              </label>
              <button
                onClick={copyToClipboard}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-accent hover:bg-accent-dark text-white'
                )}
              >
                {copied ? '✓ Kopiert!' : 'Kopieren'}
              </button>
            </div>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
              <pre className="text-sm font-mono whitespace-pre-wrap break-all">
                {output}
              </pre>
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>ℹ️ Hinweis:</strong> Alle Daten werden nur lokal in deinem Browser verarbeitet.
              Nichts wird an einen Server gesendet.
            </p>
          </div>
        </div>
      )}

      {!output && !error && (
        <div className="text-center text-gray-500 py-8">
          Wähle eine Operation und gib Daten ein
        </div>
      )}

      {/* Feature List */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-4">Features</h4>
        <ul className="space-y-2">
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>15+ Operationen:</strong> Encoding, Decoding, Hashing, Text-Transformationen
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Crypto API:</strong> Nutzt Web Crypto API für sichere Hashing-Operationen
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>Chaining:</strong> Nutze "Input/Output tauschen" für mehrere Operationen hintereinander
            </span>
          </li>
          <li className="flex items-start gap-2 text-sm">
            <span className="text-accent mt-1">✓</span>
            <span className="text-gray-700">
              <strong>100% Client-Side:</strong> Deine Daten verlassen niemals deinen Browser
            </span>
          </li>
        </ul>
      </div>

      {/* CyberChef Link */}
      <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-6">
        <h4 className="font-semibold text-lg mb-2 text-purple-900">
          🔗 Brauchst du mehr?
        </h4>
        <p className="text-sm text-purple-800 mb-3">
          Dieses Tool deckt die häufigsten Anwendungsfälle ab. Für erweiterte Operationen
          (300+ Operationen, Drag & Drop Workflow) besuche das Original:
        </p>
        <a
          href="https://gchq.github.io/CyberChef/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
        >
          CyberChef öffnen →
        </a>
      </div>
    </div>
  );
}
